// src/layouts/commit/utils/productDataHelpers.js

const createCanonicalKey = (idFieldList) => {
  if (!idFieldList || typeof idFieldList !== 'object' || Object.keys(idFieldList).length === 0) {
    return null;
  }
  return Object.keys(idFieldList)
    .sort()
    .map((key) => `${key}=${idFieldList[key]}`)
    .join('&');
};

export const areProductsEqual = (productA, productB) => {
  if (!productA || !productB) {
    return false;
  }

  // Priority 1: Match using the canonical key from IDFieldList (most reliable)
  const keyA = createCanonicalKey(productA.IDFieldList);
  const keyB = createCanonicalKey(productB.IDFieldList);
  if (keyA && keyB && keyA === keyB) {
    return true;
  }

  // Priority 2: Match using Salesforce-provided IDs (very reliable)
  const idA = productA.id || productA.external_id || productA.sfId || productA.sf_id;
  const idB = productB.id || productB.external_id || productB.sfId || productB.sf_id;
  if (idA && idB && idA === idB) {
    return true;
  }

  // Priority 3: Fallback to matching by name (less reliable but good for older data)
  if (productA.name && productB.name && productA.name === productB.name) {
    return true;
  }

  return false;
};

export const attachDataByCanonicalKey = (products, dataToAttach, attachmentProperty, dataExtractor) => {
  const defaultValue = Array.isArray(dataExtractor({})) ? [] : null;
  if (!dataToAttach || dataToAttach.length === 0) {
    return products.map((p) => ({
      ...p,
      related_data: { ...(p.related_data || {}), [attachmentProperty]: defaultValue },
    }));
  }
  const dataMap = new Map();
  dataToAttach.forEach((item) => {
    const key = createCanonicalKey(item.IDFieldList);
    if (key) dataMap.set(key, dataExtractor(item));
  });
  return products.map((p) => {
    const key = createCanonicalKey(p.IDFieldList);
    const matchingData = dataMap.get(key);
    return {
      ...p,
      related_data: { ...(p.related_data || {}), [attachmentProperty]: matchingData || defaultValue },
    };
  });
};

export const matchPricingModelsToProducts = (products, pricingModelData) => {
  const pricingModelMap = new Map();
  if (pricingModelData && pricingModelData.length > 0) {
    pricingModelData.forEach((model) => {
      const key = createCanonicalKey(model.IDFieldList);
      if (key) {
        pricingModelMap.set(key, {
          priceDimensions: model.priceDimensions || [],
          blockPrices: model.blockPrices || [],
          costs: model.costs || [],
        });
      }
    });
  }
  return products.map((product) => {
    const key = createCanonicalKey(product.IDFieldList);
    const matchingModel = pricingModelMap.get(key);
    return {
      ...product,
      related_data: {
        ...(product.related_data || {}),
        pricingModel: matchingModel || { priceDimensions: [], blockPrices: [], costs: [] },
      },
    };
  });
};

export const matchDiscountSchedulesToProducts = (products, discountSchedules) => {
  // 1. Initialize every product to have empty arrays for schedules and tiers.
  products.forEach((p) => {
    if (!p.related_data) p.related_data = {};
    p.related_data.discountSchedules = [];
    p.related_data.discountTiers = [];
  });

  if (!discountSchedules || discountSchedules.length === 0) {
    return products;
  }

  const productMap = new Map();
  products.forEach((p) => {
    const key = createCanonicalKey(p.IDFieldList);
    if (key) {
      productMap.set(key, p);
    }
  });

  discountSchedules?.forEach((schedule) => {
    if (!schedule || !schedule.IDFieldList) {
      return; // continue to next iteration
    }
    const key = createCanonicalKey(schedule.IDFieldList);
    const matchedProduct = productMap.get(key);

    if (matchedProduct) {
      const scheduleShell = { ...schedule };
      delete scheduleShell.discountTiers;

      matchedProduct.related_data.discountSchedules.push(scheduleShell);

      if (schedule.discountTiers && schedule.discountTiers.length > 0) {
        matchedProduct.related_data.discountTiers.push(...schedule.discountTiers);
      }
    }
  });

  return products;
};

export const matchConsumptionToProducts = (products, consumptionData) => {
  if (!consumptionData) return products;
  const consumptionMap = new Map();
  if (consumptionData.consumption && consumptionData.consumption.length > 0) {
    consumptionData.consumption.forEach((item) => {
      if (item.productName) consumptionMap.set(item.productName, item);
    });
  }
  return products?.map((product) => {
    const match = consumptionMap.get(product.name);
    return {
      ...product,
      related_data: {
        ...(product.related_data || {}),
        consumptionSchedules: match?.consumptionSchedules || null,
        consumptionRates: match?.consumptionRates || [],
      },
    };
  });
};
